import { motion } from 'framer-motion'
import { 
  PawPrint, 
  Instagram, 
  Facebook, 
  Twitter, 
  Mail,
  MessageCircle,
  FileText,
  Info,
  Lock
} from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    producto: [
      { label: 'Características', href: '#demo-section' },
      { label: 'Precios', href: '#pricing' },
      { label: 'Demo', href: '#demo-section' },
      { label: 'Veterinarios', href: '#vets' }
    ],
    soporte: [
      { label: 'Centro de Ayuda', href: '/help' },
      { label: 'Contacto', href: '/contact' },
      { label: 'FAQ', href: '#faq' }
    ],
    legal: [
      { label: 'Términos de Servicio', href: '/terms-of-service' },
      { label: 'Política de Privacidad', href: '/privacy-policy' },
      { label: 'Eliminación de Datos', href: '/data-deletion' }
    ]
  };

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Mail, href: 'mailto:contacto@pdogscanai.com', label: 'Email' }
  ];

  return (
    <footer className="bg-gradient-to-b from-amber-50 to-white py-6 sm:py-8 lg:py-12">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-10 
                       pb-6 sm:pb-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <PawPrint className="h-7 w-7 sm:h-9 sm:w-9 text-orange-600" />
              <span className="text-xl sm:text-2xl font-semibold text-orange-900">DogScan AI</span>
            </div>
            <p className="text-sm sm:text-base text-amber-700 mb-4 sm:mb-6 max-w-sm">
              Cuidando la salud de tu mascota con tecnología avanzada y atención veterinaria profesional 24/7.
            </p>
            {/* Contact Badge */}
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-amber-100/50 
                           px-3 sm:px-4 py-2 sm:py-2.5 rounded-full">
              <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              <span className="text-sm sm:text-base text-amber-700">Soporte: contacto@dogscanai.com</span>
            </div>
          </div>
  
          {/* Links Columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:col-span-3 gap-6 sm:gap-8">
            <div>
              <h3 className="font-semibold text-amber-900 text-base sm:text-lg mb-3 sm:mb-4">
                Producto
              </h3>
              <ul className="space-y-2.5 sm:space-y-3">
                {footerLinks.producto.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-amber-600 hover:text-amber-800 text-sm sm:text-base transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 text-base sm:text-lg mb-3 sm:mb-4">
                Soporte
              </h3>
              <ul className="space-y-2.5 sm:space-y-3">
                {footerLinks.soporte.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-amber-600 hover:text-amber-800 text-sm sm:text-base transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 text-base sm:text-lg mb-3 sm:mb-4">
                Legal
              </h3>
              <ul className="space-y-2.5 sm:space-y-3">
                {footerLinks.legal.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-amber-600 hover:text-amber-800 text-sm sm:text-base transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
  
        {/* Bottom Bar */}
        <div className="pt-6 sm:pt-8 border-t border-amber-100">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-8">
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Social Links */}
              <div className="flex items-center gap-3 sm:gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="text-amber-600 hover:text-amber-800 transition-colors p-1.5"
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </a>
                ))}
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-amber-600 text-sm sm:text-base">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <Lock className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>SSL Secure</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-2.5">
                <Info className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>ISO 27001</span>
              </div>
            </div>
  
            {/* Copyright */}
            <p className="text-sm sm:text-base text-amber-600 text-center sm:text-right">
              © {currentYear} DogScan AI. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;